import React, { useState, useEffect } from 'react';
import SousNavPV from './SousNavPV';
import { fetchRealisations, mapReal } from './realData';
import { Link, useParams, Navigate } from 'react-router-dom';
import { SiteLayout } from './SiteLayout';
import { STOCK } from './stockPhotos';

const IMG = (n) => `${process.env.PUBLIC_URL}/img/${n}.webp`;

export const TYPES = {
  ombrieres: {
    nom: 'Ombrières photovoltaïques',
    court: 'Ombrières',
    img: IMG('ombriere-dessous'),
    img2: IMG('ch-ombriere-pose'),
    intro: "Ombrières de parking pour sites industriels, logistiques, commerciaux et tertiaires. Nous réalisons le montage, la pose des modules en hauteur et l'ensemble du câblage pour le compte de l'EPC.",
    points: [
      ['Travail en hauteur maîtrisé', 'Pose depuis nacelle, balisage de la zone et coactivité gérée avec le site en exploitation.'],
      ['Modules bifaciaux', 'Manipulation et fixation adaptées aux modules verre-verre, respect des couples de serrage.'],
      ['Cheminements soignés', 'Câblage DC sous structure, goulottes et descentes propres, repérage des strings.'],
    ],
    presta: ['Montage de la charpente acier', 'Pose des modules bifaciaux', 'Câblage DC inter-modules', 'Cheminements & descentes', 'Liaisons vers onduleurs', 'Contrôles & essais'],
    ref: null,
  },
  'hangars-agricoles': {
    nom: 'Hangars agricoles',
    court: 'Hangars',
    img: IMG('hangar-vue'),
    img2: IMG('ch-toiture-filets'),
    intro: "Centrales en toiture de hangars agricoles neufs ou existants, bac acier simple ou double pente. Chantiers en zone rurale, sécurisés et organisés pour ne pas gêner l'exploitation.",
    points: [
      ['Sécurité en toiture', 'Filets en sous-face, lignes de vie, protections de rive et EPI pour chaque intervenant.'],
      ['Pose sur bac acier', 'Fixations adaptées au profil de bac, étanchéité préservée, calepinage respecté.'],
      ['Autonomie sur site', 'Équipes équipées et mobiles, habituées aux chantiers éloignés.'],
    ],
    presta: ['Implantation des rails', 'Pose des modules', 'Tirage des câbles DC en toiture', 'Chemins de câbles le long des portiques', 'Raccordement onduleurs', 'Mise à la terre & essais'],
    ref: null,
  },
  'toitures-industrielles': {
    nom: 'Toitures industrielles & tertiaires',
    court: 'Toitures',
    img: IMG('toiture-industrielle'),
    img2: IMG('ch-toiture-plate'),
    intro: "Toitures d'entrepôts, de sites industriels et de bâtiments commerciaux, en surimposition sur bac acier ou sur toiture plate avec systèmes lestés.",
    points: [
      ['Toitures plates & lestées', 'Mise en œuvre des systèmes à lest (type Esdec FlatFix) sans percement de l\'étanchéité.'],
      ['Grandes surfaces', 'Organisation du chantier par zones pour tenir les cadences sur plusieurs centaines de kWc.'],
      ['Sites en activité', 'Respect des règles du site, des accès et de la coactivité avec les occupants.'],
    ],
    presta: ['Calepinage & implantation', 'Pose des structures', 'Pose des modules', 'Cheminements DC en toiture', 'Coffrets & liaisons AC', 'Autocontrôles & DOE photo'],
    ref: null,
  },
  'centrales-au-sol': {
    nom: 'Centrales au sol',
    court: 'Centrales au sol',
    img: STOCK.centraleSol,
    img2: STOCK.centraleSol2,
    intro: "Renfort d'équipes pour la pose des tables, des modules et le câblage des centrales photovoltaïques au sol, en lien avec l'EPC et le génie civil.",
    points: [
      ['Cadences élevées', 'Équipes structurées pour la pose en série des tables et des modules.'],
      ['Câblage & repérage', 'Câblage des strings, repérage rigoureux et cheminements vers les boîtes de jonction.'],
      ['Qualité tracée', 'Autocontrôles et photos par zone pour faciliter la réception.'],
    ],
    presta: ['Montage des tables', 'Pose des modules', 'Câblage des strings', 'Cheminements & repérage', 'Mise à la terre', 'Contrôles avant mise en service'],
    ref: null,
  },
};

/* Pages « type de chantier × région » (référencement local) */
export const REGIONS = {
  'nouvelle-aquitaine': ['Nouvelle-Aquitaine', ['Gironde (33)', 'Lot-et-Garonne (47)', 'Landes (40)', 'Pyrénées-Atlantiques (64)', 'Dordogne (24)', 'Charente (16)', 'Charente-Maritime (17)', 'Vienne (86)', 'Deux-Sèvres (79)', 'Haute-Vienne (87)', 'Corrèze (19)', 'Creuse (23)']],
  occitanie: ['Occitanie', ['Haute-Garonne (31)', 'Tarn-et-Garonne (82)', 'Gers (32)', 'Tarn (81)', 'Aude (11)', 'Hérault (34)', 'Gard (30)', 'Pyrénées-Orientales (66)', 'Aveyron (12)', 'Lot (46)', 'Hautes-Pyrénées (65)', 'Ariège (09)', 'Lozère (48)']],
  'ile-de-france': ['Île-de-France', ['Paris (75)', 'Seine-et-Marne (77)', 'Yvelines (78)', 'Essonne (91)', 'Hauts-de-Seine (92)', 'Seine-Saint-Denis (93)', 'Val-de-Marne (94)', "Val-d'Oise (95)"]],
  'auvergne-rhone-alpes': ['Auvergne-Rhône-Alpes', ['Rhône (69)', 'Isère (38)', 'Ain (01)', 'Loire (42)', 'Drôme (26)', 'Ardèche (07)', 'Savoie (73)', 'Haute-Savoie (74)', 'Puy-de-Dôme (63)', 'Allier (03)', 'Cantal (15)', 'Haute-Loire (43)']],
  'provence-alpes-cote-dazur': ["Provence-Alpes-Côte d'Azur", ['Bouches-du-Rhône (13)', 'Var (83)', 'Vaucluse (84)', 'Alpes-Maritimes (06)', 'Alpes-de-Haute-Provence (04)', 'Hautes-Alpes (05)']],
  'pays-de-la-loire': ['Pays de la Loire', ['Loire-Atlantique (44)', 'Vendée (85)', 'Maine-et-Loire (49)', 'Sarthe (72)', 'Mayenne (53)']],
  bretagne: ['Bretagne', ['Ille-et-Vilaine (35)', 'Morbihan (56)', 'Finistère (29)', "Côtes-d'Armor (22)"]],
  normandie: ['Normandie', ['Seine-Maritime (76)', 'Calvados (14)', 'Manche (50)', 'Eure (27)', 'Orne (61)']],
  'hauts-de-france': ['Hauts-de-France', ['Nord (59)', 'Pas-de-Calais (62)', 'Somme (80)', 'Oise (60)', 'Aisne (02)']],
  'grand-est': ['Grand Est', ['Bas-Rhin (67)', 'Haut-Rhin (68)', 'Moselle (57)', 'Marne (51)', 'Meurthe-et-Moselle (54)', 'Vosges (88)', 'Aube (10)', 'Ardennes (08)', 'Meuse (55)', 'Haute-Marne (52)']],
  'bourgogne-franche-comte': ['Bourgogne-Franche-Comté', ["Côte-d'Or (21)", 'Saône-et-Loire (71)', 'Doubs (25)', 'Yonne (89)', 'Jura (39)', 'Nièvre (58)', 'Haute-Saône (70)', 'Territoire de Belfort (90)']],
  'centre-val-de-loire': ['Centre-Val de Loire', ['Loiret (45)', 'Indre-et-Loire (37)', 'Eure-et-Loir (28)', 'Loir-et-Cher (41)', 'Cher (18)', 'Indre (36)']],
  corse: ['Corse', ['Corse-du-Sud (2A)', 'Haute-Corse (2B)']],
};

const MATCH = { ombrieres: /ombri/i, 'hangars-agricoles': /hangar|agricol/i, 'toitures-industrielles': /toiture|industri|tertiaire/i, 'centrales-au-sol': /\bsol\b/i };

export function PageTypeChantier() {
  const { typeId, region } = useParams();
  const reg = region ? REGIONS[region] : null;
  const [ref, setRef] = useState(null);
  // Référence = dernière réalisation de ce type publiée depuis l'espace admin
  useEffect(() => {
    setRef(null);
    const m = MATCH[typeId];
    if (!m) return;
    fetchRealisations().then(d => {
      const r = d.map(mapReal).find(x => m.test(x.type));
      if (r) setRef([r.puissance, r.lieu && `à ${r.lieu}`, r.modules, r.systeme && `système ${r.systeme}`, r.duree && `en ${r.duree}`].filter(Boolean).join(' — '));
    });
  }, [typeId]);
  const t0 = TYPES[typeId];
  const t = t0 && { ...t0, ref };
  if (!t) return <Navigate to="/nos-activites" replace />;
  if (region && !reg) return <Navigate to={`/sous-traitance/${typeId}`} replace />;
  const titre = reg ? `${t.nom} en ${reg[0]}` : t.nom;
  const autres = Object.entries(TYPES).filter(([k]) => k !== typeId);

  return (
    <SiteLayout noindex={!!reg} title={reg ? `${t.court} en ${reg[0]} : sous-traitance PV` : undefined} description={reg ? `Sous-traitance photovoltaïque ${t.court.toLowerCase()} en ${reg[0]} (${reg[1].slice(0, 4).join(', ')}…) : pose, câblage et mise en service pour EPC et installateurs. Chiffrage sous 48 h.` : undefined}>
      <section style={{ position: 'relative', padding: '130px 0 110px', background: 'var(--ev-navy2)', overflow: 'hidden' }}>
        <img src={t.img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: .4 }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,rgba(7,18,58,.95),rgba(7,18,58,.45))' }} />
        <div className="ev-wrap" style={{ position: 'relative' }}>
          <span className="ev-kicker" style={{ color: 'var(--ev-sun)' }}>Sous-traitance photovoltaïque{reg ? ` · ${reg[0]}` : ''}</span>
          <h1 className="ev-h1" style={{ fontWeight: 900, color: '#fff', textTransform: 'uppercase' }}>{titre}</h1>
          <p style={{ color: 'rgba(255,255,255,.78)', maxWidth: 640, lineHeight: 1.85, margin: '18px 0 32px' }}>{reg ? `Nos équipes interviennent en ${reg[0]} pour les EPC et installateurs : ${t.court.toLowerCase()} de 100 kWc à 1 MWc, de la pose à la mise en service. ` : ''}{t.intro}</p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Link to="/devis" className="ev-btn ev-btn-sun">Nous consulter →</Link>
            <Link to="/#videos" className="ev-btn ev-btn-ghost">▶ Voir nos chantiers</Link>
          </div>
        </div>
      </section>
      <SousNavPV />

      <section className="ev-sec">
        <div className="ev-wrap" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 56, alignItems: 'center' }}>
          <img src={t.img2} alt={t.nom} loading="lazy" style={{ width: '100%', height: 460, objectFit: 'cover', boxShadow: '0 24px 60px rgba(11,27,77,.18)' }} />
          <div>
            <span className="ev-kicker">Notre savoir-faire</span>
            <h2 className="ev-title">Ce qui fait la différence <span>sur ce type de chantier</span></h2>
            <div style={{ marginTop: 26 }}>
              {t.points.map(([a, b], k) => (
                <div key={a} style={{ display: 'flex', gap: 18, padding: '18px 0', borderBottom: '1px solid var(--ev-line)' }}>
                  <span style={{ fontFamily: 'var(--ev-h)', fontWeight: 900, fontSize: '1.4rem', color: 'var(--ev-sun)', minWidth: 36 }}>0{k + 1}</span>
                  <div><h3 style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--ev-navy)', marginBottom: 6 }}>{a}</h3><p style={{ color: 'var(--ev-muted)', lineHeight: 1.75 }}>{b}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ev-sec alt">
        <div className="ev-wrap">
          <span className="ev-kicker">Prestations</span>
          <h2 className="ev-title">Ce que nous réalisons <span>pour vous</span></h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14, marginTop: 34 }}>
            {t.presta.map(x => (
              <div key={x} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '20px 22px', background: '#fff', borderLeft: '4px solid var(--ev-sun)', boxShadow: '0 4px 18px rgba(11,27,77,.06)', fontWeight: 600, color: 'var(--ev-navy)' }}>
                <span style={{ color: 'var(--ev-blue)' }}>✓</span>{x}
              </div>
            ))}
          </div>
          {t.ref && (
            <div style={{ marginTop: 34, padding: '22px 26px', background: 'var(--ev-navy)', color: '#fff', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--ev-h)', fontWeight: 800, color: 'var(--ev-sun)', letterSpacing: '.1em', fontSize: '.78rem', textTransform: 'uppercase' }}>Référence</span>
              <span style={{ fontWeight: 600 }}>{t.ref}</span>
              <Link to="/nos-realisations" style={{ marginLeft: 'auto', color: 'var(--ev-sun)', fontWeight: 700, textDecoration: 'none' }}>Toutes nos réalisations →</Link>
            </div>
          )}
        </div>
      </section>

      <section className="ev-sec">
        <div className="ev-wrap">
          <span className="ev-kicker">Autres chantiers</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 18, marginTop: 16 }}>
            {autres.map(([k, x]) => (
              <Link key={k} to={`/sous-traitance/${k}`} style={{ position: 'relative', display: 'block', height: 200, overflow: 'hidden', textDecoration: 'none' }}>
                <img src={x.img} alt={x.nom} loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(7,18,58,.9),rgba(7,18,58,.1))' }} />
                <div style={{ position: 'absolute', left: 20, bottom: 18, color: '#fff', fontFamily: 'var(--ev-h)', fontWeight: 800, fontSize: '1.1rem' }}>{x.nom} →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="ev-sec">
        <div className="ev-wrap">
          {reg ? <>
            <span className="ev-kicker">Zone d'intervention</span>
            <h2 className="ev-title">{t.court} en {reg[0]} : <span>départements couverts</span></h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 20 }}>{reg[1].map(d => <span key={d} style={{ padding: '8px 14px', background: 'var(--ev-bg2)', borderLeft: '3px solid var(--ev-sun)', fontWeight: 600, color: 'var(--ev-navy)', fontSize: 14 }}>📍 {d}</span>)}</div>
            <p style={{ color: 'var(--ev-muted)', marginTop: 18, maxWidth: 760, lineHeight: 1.8 }}>Équipes mobiles et autonomes (véhicules, outillage, EPI, nacelle si besoin), habituées aux chantiers éloignés : nous organisons hébergement et logistique pour tenir la cadence de 500 kWc en 12 à 15 jours, en {reg[0]} comme partout en France.</p>
          </> : <>
            <span className="ev-kicker">Partout en France</span>
            <h2 className="ev-title">{t.court} : <span>par région</span></h2>
          </>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 22 }}>
            {Object.entries(REGIONS).filter(([k]) => k !== region).map(([k, [nom]]) => <Link key={k} to={`/sous-traitance/${typeId}/${k}`} style={{ padding: '7px 12px', border: '1px solid var(--ev-line)', color: 'var(--ev-blue)', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>{t.court} en {nom}</Link>)}
          </div>
        </div>
      </section>

      <section className="ev-sec dark">
        <div className="ev-wrap ev-center">
          <h2 className="ev-title">Un projet de {t.court.toLowerCase()}{reg ? ` en ${reg[0]}` : ''} ? <span>Parlons-en</span></h2>
          <p className="ev-lead">Transmettez-nous plans et planning : chiffrage sous 48 h ouvrées.</p>
          <div style={{ marginTop: 30 }}><Link to="/devis" className="ev-btn ev-btn-sun">Nous consulter →</Link></div>
        </div>
      </section>
    </SiteLayout>
  );
}

export default PageTypeChantier;
